import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input, Label } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Flame, MapPin, Boxes, GitBranch, Check, X, Plus, SlidersHorizontal, History, Download, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import { HazardTimeline } from "../components/HazardTimeline";
import { Facility360 } from "../components/Facility360";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BAND_TONE = { Low: "outline", Medium: "warning", "Medium-High": "warning", High: "danger", Critical: "danger" };
const LVL = (n) => ["", "Very low", "Low", "Moderate", "Elevated", "Severe"][n] || "n/a";

function HazardBadge({ code, level }) {
  const tone = level >= 4 ? "danger" : level === 3 ? "warning" : "outline";
  return <Badge variant={tone} className="text-[9px] font-mono">{code}:{level}</Badge>;
}

function MapView({ locations, exposures }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false }).setView([8, 108], 4);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);
    const locByKey = Object.fromEntries(locations.map((l) => [l.key, l]));
    const dot = (color) => L.divIcon({ className: "", html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.5)"></div>` });
    exposures.facilities.filter((f) => f.lat != null).forEach((f) => {
      const loc = locByKey[f.location_key];
      const lv = loc ? Object.entries(loc.levels || {}).map(([c, v]) => `${c}: ${v}/5`).join(", ") : "no levels";
      L.marker([f.lat, f.lng], { icon: dot("#00E65B") }).addTo(map)
        .bindPopup(`<b>${f.name}</b><br/>Facility${f.subsidiary ? " · " + f.subsidiary : ""}<br/>${lv}`);
    });
    exposures.supplier_sites.forEach((s) => {
      const loc = locByKey[s.location_key];
      if (!loc) return;
      L.marker([loc.lat, loc.lng], { icon: dot("#FFB800") }).addTo(map)
        .bindPopup(`<b>${s.name}</b><br/>Supplier site · buffer ${s.buffer_days}d · ${s.alternatives_count} alt`);
    });
    exposures.routes.forEach((r) => {
      const pts = r.nodes.map((n) => locByKey[n.location_key]).filter(Boolean).map((l) => [l.lat, l.lng]);
      if (pts.length > 1) L.polyline(pts, { color: "#38BDF8", weight: 2, dashArray: "6 6" }).addTo(map);
      pts.forEach((p, i) => L.marker(p, { icon: dot("#38BDF8") }).addTo(map)
        .bindPopup(`<b>${r.name}</b><br/>Route node: ${r.nodes[i]?.name}`));
    });
    return () => map.remove();
  }, [locations, exposures]);
  return <div ref={ref} className="h-[420px] rounded-sm border border-border" data-testid="hazard-map" />;
}

const CELL_TONE = {
  Critical: "bg-red-500/25 border-red-500/60 text-red-300",
  High: "bg-red-500/15 border-red-500/50 text-red-300",
  "Medium-High": "bg-orange-500/15 border-orange-500/50 text-orange-300",
  Medium: "bg-yellow-500/10 border-yellow-500/40 text-yellow-300",
  Low: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
};
const EXPOSURE_LABEL = { facility: "Facility", supplier_site: "Supplier site", route: "Route" };

const PNG_TONE = { Low: "#107a57", Medium: "#a17908", "Medium-High": "#b4531e", High: "#b92e2e", Critical: "#8c1414" };

function HeatmapGrid({ catalog, computed, masterFacs, onFacility, onCell, tenantName }) {
  const rows = useMemo(() => {
    const m = new Map();
    computed.forEach((r) => {
      const k = `${r.exposure_type}|${r.exposure_name}`;
      if (!m.has(k)) m.set(k, { name: r.exposure_name, type: r.exposure_type, id: r.exposure_id, cells: {}, max: 0 });
      const row = m.get(k);
      row.cells[r.hazard_code] = r;
      if (r.score > row.max) row.max = r.score;
    });
    return [...m.values()].sort((a, b) => b.max - a.max);
  }, [computed]);

  const exportPng = () => {
    const nameW = 300, cellW = 130, rowH = 46, pad = 24, headH = 92;
    const canvas = document.createElement("canvas");
    canvas.width = pad * 2 + nameW + cellW * catalog.length;
    canvas.height = headH + rowH * (rows.length + 1) + 70;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0e100f"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f5f5f4"; ctx.font = "bold 20px sans-serif";
    ctx.fillText("Portfolio heatmap — exposure × hazard", pad, pad + 18);
    ctx.fillStyle = "#a8a29e"; ctx.font = "12px sans-serif";
    ctx.fillText(`${tenantName || "Organisation"}  ·  Generated ${new Date().toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}`, pad, pad + 40);
    let y = headH;
    ctx.font = "bold 12px monospace"; ctx.fillStyle = "#a8a29e";
    ctx.fillText("EXPOSURE", pad, y + 16);
    catalog.forEach((h, i) => ctx.fillText(`${h.code} ${h.name.slice(0, 12)}`, pad + nameW + i * cellW + 8, y + 16));
    y += 28;
    rows.forEach((row) => {
      ctx.fillStyle = "#f5f5f4"; ctx.font = "13px sans-serif";
      ctx.fillText(row.name.slice(0, 36), pad, y + 20);
      ctx.fillStyle = "#78716c"; ctx.font = "10px sans-serif";
      ctx.fillText(EXPOSURE_LABEL[row.type] || row.type, pad, y + 34);
      catalog.forEach((h, i) => {
        const c = row.cells[h.code];
        const x = pad + nameW + i * cellW;
        if (c) {
          ctx.fillStyle = PNG_TONE[c.band] || "#44403c";
          ctx.fillRect(x, y + 4, cellW - 10, rowH - 10);
          ctx.fillStyle = "#ffffff"; ctx.font = "bold 14px monospace";
          ctx.fillText(String(c.score), x + 10, y + 22);
          ctx.font = "10px sans-serif";
          ctx.fillText(c.band, x + 10, y + 34);
        } else {
          ctx.fillStyle = "#44403c"; ctx.font = "13px sans-serif";
          ctx.fillText("—", x + 10, y + 26);
        }
      });
      y += rowH;
    });
    y += 16;
    let lx = pad;
    Object.entries(PNG_TONE).forEach(([b, col]) => {
      ctx.fillStyle = col; ctx.fillRect(lx, y, 100, 22);
      ctx.fillStyle = "#fff"; ctx.font = "11px sans-serif"; ctx.fillText(b, lx + 8, y + 15);
      lx += 108;
    });
    canvas.toBlob((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(tenantName || "portfolio").replace(/[^a-z0-9]+/gi, "-")}-heatmap.png`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Heatmap PNG downloaded");
    });
  };

  const exportPdf = async () => {
    try {
      const r = await api.get("/hazards/heatmap/export.pdf", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${(tenantName || "portfolio").replace(/[^a-z0-9]+/gi, "-")}-heatmap.pdf`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Heatmap PDF downloaded");
    } catch { toast.error("Could not generate the heatmap PDF"); }
  };

  if (!rows.length) {
    return <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="hz-heatmap-empty">No computed exposure yet — place facilities at curated locations to populate the heatmap.</div>;
  }
  return (
    <div className="card-flat p-4" data-testid="hz-heatmap">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="label-eyebrow">Portfolio heatmap — exposure × hazard</div>
          <p className="text-[11px] text-muted-foreground">Sorted by highest score. Click a facility name for its 360°; click a cell for the derivation.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-2 text-[9px]">
            {["Low", "Medium", "Medium-High", "High"].map((b) => (
              <span key={b} className={`px-1.5 py-0.5 rounded-sm border ${CELL_TONE[b]}`}>{b}</span>
            ))}
          </div>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={exportPng} data-testid="hz-heatmap-export-png"><Download className="h-3 w-3" /> PNG</Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={exportPdf} data-testid="hz-heatmap-export-pdf"><FileDown className="h-3 w-3" /> PDF</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left font-mono text-[10px] text-muted-foreground uppercase py-1.5 pr-3">Exposure</th>
              {catalog.map((h) => (
                <th key={h.code} className="font-mono text-[10px] text-muted-foreground py-1.5 px-1 min-w-[92px]">{h.code}<div className="font-normal normal-case text-[9px]">{h.name}</div></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.type}|${row.name}`} className="border-t border-border" data-testid={`hz-heat-row-${row.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <td className="py-1.5 pr-3">
                  {row.type === "facility" && masterFacs.some((x) => x.id === row.id) ? (
                    <button className="font-medium underline decoration-dotted underline-offset-2 hover:text-accent text-left" onClick={() => onFacility(row.id)} data-testid={`hz-heat-fac-${row.id}`}>{row.name}</button>
                  ) : <span className="font-medium">{row.name}</span>}
                  <div className="text-[9px] text-muted-foreground">{EXPOSURE_LABEL[row.type] || row.type}</div>
                </td>
                {catalog.map((h) => {
                  const c = row.cells[h.code];
                  return (
                    <td key={h.code} className="px-1 py-1">
                      {c ? (
                        <button onClick={() => onCell(c)} data-testid={`hz-heat-cell-${row.id}-${h.code}`}
                          className={`w-full rounded-sm border px-1.5 py-1.5 text-center font-mono transition-transform hover:scale-[1.04] ${CELL_TONE[c.band] || CELL_TONE.Low}`}
                          title={`${c.band} · ${c.score}${c.dollar_impact_sgd ? ` · S$${c.dollar_impact_sgd.toLocaleString()}` : ""}`}>
                          <div className="font-bold">{c.score}</div>
                          <div className="text-[9px] opacity-80">{c.band}</div>
                        </button>
                      ) : <div className="w-full text-center text-muted-foreground/40 py-2">—</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Derivation({ d }) {
  if (!d) return null;
  return (
    <div className="space-y-1">
      {(d.inputs || []).map((i, k) => (
        <div key={k} className="flex justify-between gap-3 text-[11px] font-mono">
          <span className="text-muted-foreground">{i.input} <span className="opacity-60">({i.direction}, w={i.weight})</span></span>
          <span>{i.value} → +{i.contribution}</span>
        </div>
      ))}
      {d.impact_formula && <div className="text-[11px] font-mono text-accent">$ impact = {d.impact_formula}</div>}
      <div className="text-[10px] text-muted-foreground">Hazard level source: shared platform Hazard Catalog · curated 0–5 level at the matched location (reference data, identical for every tenant).</div>
      <div className="text-[10px] text-muted-foreground italic">{d.method}</div>
    </div>
  );
}

export default function HazardRisk() {
  const { user, tenant } = useAuth();
  const canEdit = ["tenant_admin", "esg_manager", "super_admin"].includes(user?.role);
  const [catalog, setCatalog] = useState([]);
  const [locations, setLocations] = useState([]);
  const [exp, setExp] = useState({ facilities: [], supplier_sites: [], routes: [], unplaced: 0 });
  const [risks, setRisks] = useState({ risks: [], candidates: [], weights: {} });
  const [legacy, setLegacy] = useState([]);
  const [openRisk, setOpenRisk] = useState(null);
  const [siteOpen, setSiteOpen] = useState(false);
  const [site, setSite] = useState({ name: "", supplier_name: "", location_key: "", buffer_days: 14, alternatives_count: 0 });
  const [wOpen, setWOpen] = useState(false);
  const [w, setW] = useState({ hazard: 0.5, buffer: 0.25, alternatives: 0.25 });
  const [tlHazard, setTlHazard] = useState(null);
  const [masterFacs, setMasterFacs] = useState([]);
  const [f360, setF360] = useState(null);
  const [covReqs, setCovReqs] = useState([]);

  const load = () => {
    api.get("/hazards/catalog").then((r) => setCatalog(r.data.hazards)).catch(() => {});
    api.get("/hazards/locations").then((r) => setLocations(r.data.locations)).catch(() => {});
    api.get("/hazards/exposures").then((r) => setExp(r.data)).catch(() => {});
    api.get("/hazards/risks").then((r) => { setRisks(r.data); setW(r.data.weights); }).catch(() => {});
    api.get("/hazards/legacy-events").then((r) => setLegacy(r.data.events)).catch(() => {});
    api.get("/facilities").then((r) => setMasterFacs(r.data.facilities)).catch(() => {});
    api.get("/hazards/coverage-requests").then((r) => setCovReqs(r.data.requests)).catch(() => {});
  };
  useEffect(load, []);

  const requestCoverage = async (f) => {
    try {
      const r = await api.post("/hazards/coverage-requests", { name: f.name, lat: f.lat, lng: f.lng, facility_id: f.id });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const decide = async (c, action) => {
    try {
      await api.post(`/hazards/candidates/${c.id}/${action}`, {});
      toast.success(action === "confirm" ? "Risk confirmed into the register" : "Candidate dismissed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addSite = async () => {
    try {
      await api.post("/hazards/exposures/supplier-sites", site);
      toast.success("Supplier site added"); setSiteOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveWeights = async () => {
    try {
      await api.put("/hazards/config", { weights: { hazard: +w.hazard, buffer: +w.buffer, alternatives: +w.alternatives } });
      toast.success("Scoring weights updated"); setWOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pending = useMemo(() => risks.candidates.filter((c) => c.status === "pending"), [risks]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="hazard-risk-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-1">Scenarios &amp; Risks · Physical Climate</div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
            <Flame className="h-7 w-7 text-accent" /> Hazards &amp; Locations
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            A risk is one hazard meeting one exposure at its location. Hazard levels are shared reference data —
            read live, never re-entered per tenant.
          </p>
        </div>
        {canEdit && <Button variant="outline" size="sm" onClick={() => setWOpen(true)} data-testid="hz-weights-btn"><SlidersHorizontal className="h-3.5 w-3.5" /> Scoring settings</Button>}
      </div>

      <Tabs defaultValue="risks">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="risks" data-testid="hz-tab-risks">Computed Risks {pending.length > 0 && <Badge variant="warning" className="ml-1 text-[9px]">{pending.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="hz-tab-heatmap">Heatmap</TabsTrigger>
          <TabsTrigger value="map" data-testid="hz-tab-map">Map &amp; Locations</TabsTrigger>
          <TabsTrigger value="exposures" data-testid="hz-tab-exposures">Exposures</TabsTrigger>
          <TabsTrigger value="catalog" data-testid="hz-tab-catalog">Hazard Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="risks" className="mt-4 space-y-4">
          {pending.map((c) => (
            <div key={c.id} className="card-flat p-4 border-[#FFB800]/50" data-testid={`hz-candidate-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="warning" className="text-[9px]">CANDIDATE — awaiting confirmation</Badge>
                    <Badge variant={BAND_TONE[c.band]} className="text-[9px]">{c.band} · {c.score}</Badge>
                  </div>
                  <div className="text-sm font-semibold mt-1">{c.hazard_name} @ {c.location_name} → {c.exposure_name}</div>
                  <p className="text-[11px] text-muted-foreground mt-1 max-w-2xl">{c.trace}</p>
                  <div className="mt-2"><Derivation d={c.derivation} /></div>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="primary" onClick={() => decide(c, "confirm")} data-testid={`hz-confirm-${c.id}`}><Check className="h-3 w-3" /> Confirm into register</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(c, "dismiss")} data-testid={`hz-dismiss-${c.id}`}><X className="h-3 w-3" /> Dismiss</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="card-flat overflow-x-auto">
            <div className="px-4 pt-3 label-eyebrow">Computed lane — hazard × exposure at location (live)</div>
            <Table>
              <TableHeader><TableRow><TableHead>Hazard</TableHead><TableHead>Exposure</TableHead><TableHead>Location</TableHead><TableHead>Level</TableHead><TableHead>Susceptibility</TableHead><TableHead>$ impact</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {risks.risks.map((r, i) => (
                  <TableRow key={i} data-testid={`hz-risk-row-${i}`}>
                    <TableCell><span className="font-mono text-[10px] text-muted-foreground">{r.hazard_code}</span> {r.hazard_name}<div className="text-[10px] text-muted-foreground">{r.horizon}</div></TableCell>
                    <TableCell className="text-xs">{r.exposure_name}<div className="text-[10px] text-muted-foreground">{r.exposure_type.replace("_", " ")}</div></TableCell>
                    <TableCell className="text-xs">{r.location_name}</TableCell>
                    <TableCell><HazardBadge code={r.hazard_code} level={r.hazard_level} /></TableCell>
                    <TableCell><Badge variant={BAND_TONE[r.band]} className="text-[9px]">{r.band} · {r.score}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{r.dollar_impact_sgd ? `S$${r.dollar_impact_sgd.toLocaleString()}` : "—"}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setOpenRisk(r)} data-testid={`hz-risk-derivation-${i}`}>Derivation</Button></TableCell>
                  </TableRow>
                ))}
                {!risks.risks.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">No computable risks — add coordinates to facilities or create supplier sites/routes.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="heatmap" className="mt-4 space-y-4">
          <HeatmapGrid catalog={catalog} computed={risks.risks || []} masterFacs={masterFacs} tenantName={tenant?.name}
            onFacility={(fid) => { const m = masterFacs.find((x) => x.id === fid); if (m) setF360(m); }}
            onCell={(r) => setOpenRisk(r)} />
        </TabsContent>

        <TabsContent value="map" className="mt-4 space-y-4">
          <MapView locations={locations} exposures={exp} />
          <div className="card-flat overflow-x-auto">
            <div className="px-4 pt-3 label-eyebrow">Shared location hazard levels — curated reference data (Super Admin maintained)</div>
            <Table>
              <TableHeader><TableRow><TableHead>Location</TableHead><TableHead>Country</TableHead>{catalog.map((h) => <TableHead key={h.code}>{h.code}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {locations.map((l) => (
                  <TableRow key={l.key} data-testid={`hz-loc-${l.key}`}>
                    <TableCell className="text-xs font-medium">{l.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.country}</TableCell>
                    {catalog.map((h) => <TableCell key={h.code}><HazardBadge code={h.code} level={(l.levels || {})[h.code] || 0} /></TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="exposures" className="mt-4 space-y-4">
          {exp.unplaced > 0 && (
            <div className="card-flat p-3 border-[#FFB800]/50 text-xs" data-testid="hz-unplaced-warning">
              <Badge variant="warning" className="text-[9px] mr-2">not placeable — no location</Badge>
              {exp.unplaced} facilit{exp.unplaced === 1 ? "y is" : "ies are"} excluded from computation until coordinates are added (Data Management → Facilities).
            </div>
          )}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Facilities</div>
              {exp.facilities.map((f) => (
                <div key={f.id} className="py-1.5 border-b border-border last:border-0 text-xs flex justify-between gap-2" data-testid={`hz-fac-${f.id}`}>
                  <span>{f.name}{f.subsidiary && <span className="text-muted-foreground"> · {f.subsidiary}</span>}</span>
                  {f.facility_type === "coverage_route"
                    ? <Badge variant="outline" className="text-[9px]" title="Island-wide coverage — exposure assessed along the service route">coverage — route exposure</Badge>
                    : f.placeable ? <Badge variant="outline" className="text-[9px] font-mono">{f.lat?.toFixed(2)}, {f.lng?.toFixed(2)}</Badge>
                      : <span className="flex items-center gap-1">
                          <Badge variant="warning" className="text-[9px]" title={f.lat != null
                            ? `Coordinates are set (${f.lat}, ${f.lng}), but there's no curated hazard location within 100 km — verify them on a map or ask your administrator to add coverage for this area.`
                            : "No coordinates yet — open the facility's 360° Profile and enter its latitude/longitude, and hazard exposure will appear immediately."}>not placeable</Badge>
                          {f.lat != null && (
                            <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5" onClick={() => requestCoverage(f)} data-testid={`hz-request-coverage-${f.id}`} title="Ask the platform operator to add a curated hazard location for this area">
                              <Send className="h-2.5 w-2.5" /> Request coverage
                            </Button>
                          )}
                        </span>}
                </div>
              ))}
            </div>
            <div className="card-flat p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow flex items-center gap-1"><Boxes className="h-3.5 w-3.5" /> Supplier sites</div>
                {canEdit && <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setSiteOpen(true)} data-testid="hz-add-site-btn"><Plus className="h-3 w-3" /> Add</Button>}
              </div>
              {exp.supplier_sites.map((s) => (
                <div key={s.id} className="py-1.5 border-b border-border last:border-0 text-xs" data-testid={`hz-site-${s.id}`}>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{s.location_key} · buffer {s.buffer_days}d · {s.alternatives_count} qualified alternative(s)</div>
                </div>
              ))}
              {!exp.supplier_sites.length && <div className="text-xs text-muted-foreground">None yet.</div>}
            </div>
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2 flex items-center gap-1"><GitBranch className="h-3.5 w-3.5" /> Routes</div>
              {exp.routes.map((r) => (
                <div key={r.id} className="py-1.5 border-b border-border last:border-0 text-xs" data-testid={`hz-route-${r.id}`}>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] text-muted-foreground">{r.nodes.map((n) => n.name).join(" → ")}</div>
                </div>
              ))}
              {!exp.routes.length && <div className="text-xs text-muted-foreground">None yet.</div>}
            </div>
          </div>
          {covReqs.length > 0 && (
            <div className="card-flat p-4" data-testid="hz-coverage-requests">
              <div className="label-eyebrow mb-2 flex items-center gap-1"><Send className="h-3.5 w-3.5" /> Coverage requests — sent to the platform operator</div>
              {covReqs.map((r) => (
                <div key={r.id} className="py-1.5 border-b border-border last:border-0 text-xs flex justify-between gap-2 flex-wrap" data-testid={`hz-covreq-${r.id}`}>
                  <span>{r.area_name} <span className="font-mono text-[10px] text-muted-foreground">({r.lat}, {r.lng})</span>
                    {r.requesters?.length > 1 && <span className="text-[10px] text-muted-foreground"> · merged with {r.requesters.length - 1} other request(s)</span>}
                  </span>
                  <Badge variant={r.status === "fulfilled" ? "success" : r.status === "declined" ? "danger" : "warning"} className="text-[9px]">
                    {r.status}{r.status === "declined" && r.decline_reason ? ` — ${r.decline_reason}` : ""}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="catalog" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            {catalog.map((h) => (
              <div key={h.code} className="card-flat p-4 cursor-pointer hover:bg-surface-hover transition-colors" data-testid={`hz-catalog-${h.code}`}
                onClick={() => setTlHazard(h)} role="button" tabIndex={0}>
                <div className="flex items-center gap-2">
                  <Badge variant="lime" className="font-mono text-[10px]">{h.code}</Badge>
                  <span className="font-display font-bold">{h.name}</span>
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">applies to: {(h.applies_to || []).join(" & ")}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2"><span className="text-foreground font-medium">Pathway:</span> {h.pathway}</p>
                <p className="text-xs text-muted-foreground mt-1"><span className="text-foreground font-medium">Business impact:</span> {h.business_impact}</p>
                <Button size="sm" variant="ghost" className="h-6 text-[10px] mt-2 -ml-2" data-testid={`hz-timeline-${h.code}`}
                  onClick={(e) => { e.stopPropagation(); setTlHazard(h); }}>
                  <History className="h-3 w-3" /> View timeline
                </Button>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground">Shared platform catalog — view only. Maintained by the Lestari operator.</div>
          {!!legacy.length && (
            <div className="card-flat p-4" data-testid="hz-legacy-events">
              <div className="label-eyebrow mb-2">Legacy hazard events (read-only history — migrated into location levels)</div>
              {legacy.map((e) => (
                <div key={e.id} className="text-[11px] font-mono py-1 border-b border-border last:border-0 flex justify-between">
                  <span>{e.name} ({e.hazard_type}, sev {e.severity}) — {(e.countries || []).join(", ")}</span>
                  <Badge variant="outline" className="text-[9px]">{e.migrated ? "migrated" : "legacy"}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {tlHazard && <HazardTimeline hazard={tlHazard} canEdit={canEdit} onClose={() => setTlHazard(null)} />}
      {f360 && <Facility360 facility={f360} canEdit={canEdit} onClose={() => setF360(null)} onSaved={load} />}

      <Dialog open={!!openRisk} onOpenChange={(o) => !o && setOpenRisk(null)}>
        <DialogContent data-testid="hz-derivation-dialog">
          <DialogHeader><DialogTitle className="text-base">{openRisk?.hazard_name} → {openRisk?.exposure_name}</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">Hazard level read live from {openRisk?.location_name} — never stored on the risk.</div>
          <Derivation d={openRisk?.derivation} />
          {openRisk?.dollar_impact_sgd != null && <div className="font-mono text-lg font-bold text-accent">S${openRisk.dollar_impact_sgd?.toLocaleString()}</div>}
        </DialogContent>
      </Dialog>

      <Dialog open={siteOpen} onOpenChange={setSiteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add supplier site</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Site name *</Label><Input value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} data-testid="hz-site-name" /></div>
            <div className="col-span-2"><Label>Location *</Label>
              <Select value={site.location_key} onValueChange={(v) => setSite({ ...site, location_key: v })}>
                <SelectTrigger data-testid="hz-site-location"><SelectValue placeholder="Pick a curated location…" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.key} value={l.key}>{l.name} ({l.country})</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Inventory buffer (days)</Label><Input type="number" value={site.buffer_days} onChange={(e) => setSite({ ...site, buffer_days: +e.target.value })} data-testid="hz-site-buffer" /></div>
            <div><Label>Qualified alternatives</Label><Input type="number" value={site.alternatives_count} onChange={(e) => setSite({ ...site, alternatives_count: +e.target.value })} data-testid="hz-site-alts" /></div>
          </div>
          <Button variant="primary" onClick={addSite} disabled={!site.name || !site.location_key} data-testid="hz-site-save">Add site</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={wOpen} onOpenChange={setWOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Susceptibility scoring — default method, adjustable</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Management can change the method without a rebuild. Weights must sum to 1.0.</p>
          {["hazard", "buffer", "alternatives"].map((k) => (
            <div key={k}><Label className="capitalize">{k === "buffer" ? "Buffer / resilience" : k === "alternatives" ? "Alternatives / criticality" : "Hazard level"}</Label>
              <Input type="number" step="0.05" min="0" max="1" value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} data-testid={`hz-weight-${k}`} /></div>
          ))}
          <Button variant="primary" onClick={saveWeights} data-testid="hz-weights-save">Save weights</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
